import {
  isWorkoutCancelled,
  isWorkoutMissingSlotWorkout,
  resolveMissingSlotOverviewStatusView,
  resolveWorkoutOverviewStatus,
} from '~/utils/workoutOverviewStatus';
import { getWeekOpenHref } from '~/utils/trainingWeekView';
import {
  formatWorkoutDeliveryFormatShortLabel,
  formatWorkoutScheduleTimeDisplay,
  hasWorkoutScheduleTime,
  resolveWorkoutDeliveryFormatKey,
} from '~/utils/trainingDeliveryFormat';
import { resolveDefaultTimezone, resolveTodayYmd } from '~/utils/timezone';
import {
  countIsoWeekOffset,
  resolveActiveRelativeIsoWeekTabId,
  resolveDefaultSelectedWeekBounds,
  resolveRelativeIsoWeekNavBounds,
  shiftSelectedWeekBounds,
} from '~/utils/isoWeekPicker';
import {
  formatCompactWeekDateRangeLabel,
  formatRelativeIsoWeeksOffsetLabel,
} from '~/utils/weekDateRangeLabel';
import { formatTrainingDateRelativeLabel } from '~/utils/trainingDateLabel';
import {
  SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL,
  WORKOUT_SCHEDULE_DAY_UNKNOWN_HINT,
  formatWorkoutRelativeDayLabel,
  isWorkoutCalendarDayKnown,
  isWorkoutFullyUndeterminedScheduleDay,
  resolveWorkoutRelativeDayNumber,
} from '~/utils/workoutScheduleContext';

export function buildWorkoutOverviewStatusSourceFromPlanWorkout(workout) {
  if (!workout || typeof workout !== 'object') {
    return null;
  }

  if (workout.status === 'missing_slot' || workout.is_missing_slot) {
    return null;
  }

  if (workout.status === 'skipped') {
    return {
      ent_is_completed: false,
      ent_is_started: false,
      workout_sets_confirmed: false,
    };
  }

  const needsWeights = workout.prescription_needs_weights ?? workout.workout_prescription_needs_weights;
  const weightsComplete = workout.prescription_weights_complete ?? workout.workout_prescription_weights_complete;
  const isLegacyCompletedOnly = workout.status === 'completed' && workout.ent_is_completed === undefined;

  return {
    ent_is_completed: Boolean(workout.ent_is_completed) || workout.status === 'completed',
    ent_is_started: Boolean(workout.ent_is_started),
    workout_exercises_confirmed: Boolean(workout.workout_exercises_confirmed),
    workout_prescription_weights_confirmed: Boolean(workout.workout_prescription_weights_confirmed),
    workout_sets_confirmed: isLegacyCompletedOnly
      ? true
      : Boolean(workout.workout_sets_confirmed),
    workout_analysis_confirmed: Boolean(workout.workout_analysis_confirmed),
    workout_result_checked: Boolean(workout.workout_result_checked),
    workout_has_remarks: Boolean(workout.workout_has_remarks),
    prescription_needs_weights: Boolean(needsWeights),
    prescription_weights_complete: Boolean(weightsComplete),
    workout_status: workout.workout_status ?? null,
    cancel_reason: workout.cancel_reason ?? null,
  };
}

export function resolveWorkoutPlanStatusView(workout) {
  if (isWorkoutMissingSlotWorkout(workout)) {
    return resolveMissingSlotOverviewStatusView();
  }

  if (isWorkoutCancelled(workout)) {
    return resolveWorkoutOverviewStatus(workout);
  }

  return resolveWorkoutOverviewStatus(buildWorkoutOverviewStatusSourceFromPlanWorkout(workout));
}

export function resolveWorkoutPlanRowHref(workout) {
  const workoutUrl = String(workout?.url ?? '').trim();
  if (workoutUrl) {
    return workoutUrl;
  }

  const weekUrl = String(workout?.week_open_url ?? '').trim();
  if (weekUrl) {
    return weekUrl;
  }

  return null;
}

export function isWorkoutMissingPlanSlot(workout) {
  return isWorkoutMissingSlotWorkout(workout);
}

export function isWorkoutRealPlanEntity(workout) {
  if (!workout || isWorkoutMissingPlanSlot(workout)) {
    return false;
  }
  const uuid = String(workout.workout_uuid ?? workout.uuid ?? workout.id ?? '').trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
}

export function collectEmptyPlanWorkouts(workouts) {
  return (workouts || []).filter(isWorkoutMissingPlanSlot);
}

export function resolveWeekEntityUuidFromPlanWeek(week) {
  const direct = String(week?.week_entity_uuid ?? week?.week_uuid ?? '').trim();
  if (direct) {
    return direct;
  }
  if (week?.orphan) {
    return String(week?.id ?? '').trim();
  }
  return '';
}

export function buildWeekFillContext(program, week, workouts) {
  const programUuid = String(program?.program_uuid ?? program?.id ?? '').trim() || null;
  const weekEntityUuid = resolveWeekEntityUuidFromPlanWeek(week) || null;
  const builderWeekId = String(week?.builder_week_id ?? '').trim() || null;
  const emptyWorkouts = collectEmptyPlanWorkouts(workouts);
  const isSkip = isSkipWeek(week);

  return {
    programUuid,
    weekEntityUuid,
    builderWeekId,
    weekTitle: String(week?.label ?? week?.title ?? '').trim(),
    isSkipWeek: isSkip,
    emptyWorkouts,
    emptyWorkoutCount: emptyWorkouts.length,
    hasEmptyWorkouts: emptyWorkouts.length > 0,
    canFillWeek: !isSkip && Boolean(programUuid || weekEntityUuid),
    canFillWorkout: emptyWorkouts.length > 0,
    showFillAction: !isSkip && emptyWorkouts.length > 0,
  };
}

export function resolveFirstEmptyWorkoutFillContext(weekHeaders) {
  for (const weekHeader of weekHeaders || []) {
    const fillContext = weekHeader?.fillContext;
    if (fillContext?.hasEmptyWorkouts && fillContext?.canFillWorkout) {
      return fillContext;
    }
  }
  return null;
}

export function resolveWorkoutSlotIndex(workout) {
  const slotIndex = Number(
    workout?.source_program_week_slot_index
      ?? workout?.schedule_context?.week_slot_index
      ?? workout?.week_slot_index
      ?? 0,
  );
  return slotIndex > 0 ? slotIndex : null;
}

export function resolveFirstEmptyWorkoutSlotIndex(fillContext) {
  const emptyWorkout = Array.isArray(fillContext?.emptyWorkouts)
    ? fillContext.emptyWorkouts[0]
    : null;
  return resolveWorkoutSlotIndex(emptyWorkout);
}

export function resolveWeekHeaderForWorkout(weekHeaders, workout) {
  const headers = weekHeaders || [];
  const weekGroupId = String(workout?.weekGroupId ?? '').trim();
  if (weekGroupId) {
    const matched = headers.find((header) => header.id === weekGroupId);
    if (matched) {
      return matched;
    }
  }

  const workoutId = String(workout?.id ?? workout?.uuid ?? '').trim();
  if (workoutId) {
    const matchedByWorkout = headers.find((header) => (
      Array.isArray(header?.fillContext?.emptyWorkouts)
      && header.fillContext.emptyWorkouts.some((item) => (
        String(item?.id ?? item?.uuid ?? '').trim() === workoutId
      ))
    ));
    if (matchedByWorkout) {
      return matchedByWorkout;
    }
  }

  return headers.find((header) => header.fillContext?.showFillAction) || headers[0] || null;
}

export function formatEmptyWorkoutsLabel(count) {
  const value = Number(count) || 0;
  if (value === 1) {
    return '1 тренировка без упражнений';
  }
  if (value >= 2 && value <= 4) {
    return `${value} тренировки без упражнений`;
  }
  return `${value} тренировок без упражнений`;
}

export function formatEmptyPlanSlotsLabel(count) {
  const value = Number(count) || 0;
  if (value === 1) {
    return '1 слот ожидает заполнения';
  }
  if (value >= 2 && value <= 4) {
    return `${value} слота ожидают заполнения`;
  }
  return `${value} слотов ожидают заполнения`;
}

export function formatWeekFillButtonLabel(hasRealWorkouts) {
  return hasRealWorkouts ? 'Заполнить слоты' : 'Заполнить из программы';
}

export const CLIENT_TRAINING_PLAN_GENERATE_HINTS = {
  singleWorkout: 'Сгенерировать упражнения только для этой тренировки.',
  wholeWeek: 'Сгенерировать все незаполненные тренировки недели по параметрам программы.',
};

export function formatWeekGenerateButtonLabel({ loading = false, emptyCount = 0 } = {}) {
  if (loading) {
    return 'Генерация…';
  }
  const count = Number(emptyCount) || 0;
  if (count > 1) {
    return `Сгенерировать всю неделю (${count})`;
  }
  return 'Сгенерировать всю неделю';
}

export function resolveWeekFillSummary(weekHeaders) {
  const headers = weekHeaders || [];
  const emptyCount = headers.reduce(
    (sum, header) => sum + (header.fillContext?.emptyWorkoutCount ?? 0),
    0,
  );
  const primaryHeader = headers.find((header) => header.fillContext?.showFillAction) || headers[0];
  return {
    emptyCount,
    primaryHeader,
    showAction: emptyCount > 0 && Boolean(primaryHeader?.fillContext?.showFillAction),
  };
}

export function resolveSingleWeekFillSummary(weekHeader) {
  if (!weekHeader) {
    return {
      emptyCount: 0,
      primaryHeader: null,
      showAction: false,
    };
  }

  const emptyCount = weekHeader.fillContext?.emptyWorkoutCount ?? 0;
  return {
    emptyCount,
    primaryHeader: weekHeader,
    showAction: Boolean(weekHeader.fillContext?.showFillAction),
  };
}

export function resolveWeekActionsBlockTitle({
  hasRealWorkouts = false,
  showFillAction = false,
  hasWorkouts = false,
} = {}) {
  if (showFillAction) {
    return 'Другие способы';
  }
  if (!hasRealWorkouts && !hasWorkouts) {
    return 'Тренировки не добавлены';
  }
  return 'Действия с этой неделей';
}

export function resolveWorkoutPlanDateGroupKey(workout) {
  if (!isWorkoutCalendarDayKnown(workout?.schedule_context)) {
    const relativeDay = resolveWorkoutRelativeDayNumber(workout?.schedule_context);
    if (relativeDay !== null) {
      return `relative:${relativeDay}`;
    }
    if (isWorkoutFullyUndeterminedScheduleDay(workout?.schedule_context)) {
      return 'day-undetermined';
    }
    return 'day-unknown';
  }

  const dateSort = String(workout?.date_sort ?? '').trim();
  if (dateSort) {
    return dateSort;
  }

  const scheduleDate = String(workout?.schedule?.date ?? '').trim();
  if (scheduleDate) {
    return `label:${scheduleDate}`;
  }

  return 'undated';
}

export function formatPlanDateDayHeader(dateSort, viewerTodayYmd = formatTodayYmd()) {
  const raw = String(dateSort ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return {
      primary: '',
      relative: '',
      relativeHighlight: false,
    };
  }

  const date = parseYmd(raw);
  if (!date) {
    return {
      primary: '',
      relative: '',
      relativeHighlight: false,
    };
  }

  const diffDays = diffCalendarDays(raw, viewerTodayYmd);
  const weekday = WEEKDAY_SHORT[date.getDay()] || '';
  const month = SHORT_MONTHS[date.getMonth() + 1] || '';
  const primary = `${weekday}., ${date.getDate()} ${month}`;
  const relative = formatTrainingDateRelativeLabel(raw, viewerTodayYmd);

  return {
    primary,
    relative,
    relativeHighlight: diffDays === 0 || diffDays === 1,
  };
}

function resolveWorkoutPlanDateGroupMeta(workout, viewerTodayYmd = formatTodayYmd()) {
  if (!isWorkoutCalendarDayKnown(workout?.schedule_context)) {
    if (isWorkoutFullyUndeterminedScheduleDay(workout?.schedule_context)) {
      return {
        dateLabel: SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL,
        weekNote: '',
        hint: '',
        dateDetail: '',
        dateDetailHighlight: false,
      };
    }

    return {
      dateLabel: formatWorkoutRelativeDayLabel(workout?.schedule_context),
      weekNote: '',
      hint: '',
      dateDetail: '',
      dateDetailHighlight: false,
    };
  }

  const scheduleDate = String(workout?.schedule?.date ?? '').trim() || '—';
  const isFullyUndetermined = isWorkoutFullyUndeterminedScheduleDay(
    workout?.schedule_context,
    scheduleDate,
  );

  if (isFullyUndetermined) {
    return {
      dateLabel: SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL,
      weekNote: '',
      hint: '',
      dateDetail: '',
      dateDetailHighlight: false,
    };
  }

  const viewerDateYmd = String(workout?.schedule_context?.timezone_context?.viewer?.local_date ?? '').slice(0, 10);
  const dateSort = viewerDateYmd || String(workout?.date_sort ?? '').trim();
  const header = formatPlanDateDayHeader(dateSort, viewerTodayYmd);

  return {
    dateLabel: header.primary || scheduleDate,
    weekNote: '',
    hint: '',
    dateDetail: header.relative,
    dateDetailHighlight: header.relativeHighlight,
  };
}

function sortWorkoutsWithinPlanDateGroup(left, right) {
  const leftRelativeDay = resolveWorkoutRelativeDayNumber(left?.schedule_context) ?? Number.MAX_SAFE_INTEGER;
  const rightRelativeDay = resolveWorkoutRelativeDayNumber(right?.schedule_context) ?? Number.MAX_SAFE_INTEGER;
  if (leftRelativeDay !== rightRelativeDay) {
    return leftRelativeDay - rightRelativeDay;
  }

  return String(left.start_time ?? '').localeCompare(String(right.start_time ?? ''));
}

function comparePlanDateGroups(left, right) {
  const leftFirst = left?.workouts?.[0];
  const rightFirst = right?.workouts?.[0];
  const leftUnknown = !isWorkoutCalendarDayKnown(leftFirst?.schedule_context);
  const rightUnknown = !isWorkoutCalendarDayKnown(rightFirst?.schedule_context);

  if (leftUnknown !== rightUnknown) {
    return leftUnknown ? 1 : -1;
  }

  if (leftUnknown) {
    const leftFullyUndetermined = isWorkoutFullyUndeterminedScheduleDay(
      leftFirst?.schedule_context,
      left?.dateLabel,
    );
    const rightFullyUndetermined = isWorkoutFullyUndeterminedScheduleDay(
      rightFirst?.schedule_context,
      right?.dateLabel,
    );

    if (leftFullyUndetermined !== rightFullyUndetermined) {
      return leftFullyUndetermined ? 1 : -1;
    }

    const leftRelativeDay = resolveWorkoutRelativeDayNumber(leftFirst?.schedule_context) ?? Number.MAX_SAFE_INTEGER;
    const rightRelativeDay = resolveWorkoutRelativeDayNumber(rightFirst?.schedule_context) ?? Number.MAX_SAFE_INTEGER;
    if (leftRelativeDay !== rightRelativeDay) {
      return leftRelativeDay - rightRelativeDay;
    }

    return 0;
  }

  return String(leftFirst?.date_sort ?? '').localeCompare(String(rightFirst?.date_sort ?? ''));
}

export function groupWorkoutsByPlanDate(workouts, { viewerTodayYmd = formatTodayYmd() } = {}) {
  const groups = [];
  const groupIndexByKey = new Map();

  for (const workout of workouts || []) {
    const key = resolveWorkoutPlanDateGroupKey(workout);
    let groupIndex = groupIndexByKey.get(key);

    if (groupIndex === undefined) {
      groupIndex = groups.length;
      groupIndexByKey.set(key, groupIndex);
      groups.push({
        id: `${key}:${groupIndex}`,
        ...resolveWorkoutPlanDateGroupMeta(workout, viewerTodayYmd),
        workouts: [],
      });
    }

    groups[groupIndex].workouts.push(workout);
  }

  for (const group of groups) {
    group.workouts.sort(sortWorkoutsWithinPlanDateGroup);
  }

  return groups.sort(comparePlanDateGroups);
}

const SHORT_MONTHS = {
  1: 'янв',
  2: 'фев',
  3: 'мар',
  4: 'апр',
  5: 'мая',
  6: 'июн',
  7: 'июл',
  8: 'авг',
  9: 'сен',
  10: 'окт',
  11: 'ноя',
  12: 'дек',
};

const WEEKDAY_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export function formatWorkoutFormatLabel(workout) {
  if (resolveWorkoutDeliveryFormatKey(workout)) {
    return formatWorkoutDeliveryFormatShortLabel(workout);
  }

  const label = String(workout?.delivery_format_label ?? '').trim();
  if (label) {
    return label;
  }

  return 'Формат не задан';
}

function parseYmd(value) {
  const raw = String(value ?? '').slice(0, 10);
  const [year, month, day] = raw.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

const WORKOUT_SCHEDULE_DATE_PLACEHOLDER = '--.--.---';
const WORKOUT_SCHEDULE_TIME_PLACEHOLDER = '--:--';

function hasWorkoutStartTime(workout) {
  return hasWorkoutScheduleTime(workout);
}

function resolveWorkoutScheduleTimeLabel(workout) {
  const time = formatWorkoutScheduleTimeDisplay(workout);
  if (time) {
    return time;
  }
  return hasWorkoutStartTime(workout) ? WORKOUT_SCHEDULE_TIME_PLACEHOLDER : WORKOUT_SCHEDULE_TIME_PLACEHOLDER;
}

function diffCalendarDays(fromYmd, toYmd) {
  const from = parseYmd(String(fromYmd ?? '').slice(0, 10));
  const to = parseYmd(String(toYmd ?? '').slice(0, 10));
  if (!from || !to) {
    return null;
  }
  return Math.round((from - to) / 86400000);
}

export function formatPlanDateWithYear(value) {
  if (!value) {
    return null;
  }

  const raw = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const date = parseYmd(raw);
    if (!date) {
      return null;
    }
    const month = SHORT_MONTHS[date.getMonth() + 1] || '';
    return `${date.getDate()} ${month} ${date.getFullYear()}`;
  }

  return String(value).trim() || null;
}

export function formatPlanDateCompact(value) {
  if (!value) {
    return null;
  }

  const raw = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const date = parseYmd(raw);
    if (!date) {
      return null;
    }
    const month = SHORT_MONTHS[date.getMonth() + 1] || '';
    return `${date.getDate()} ${month}`;
  }

  return String(value).trim() || null;
}

export function formatWeekPeriodCompact(startYmd, endYmd) {
  const startRaw = String(startYmd ?? '').slice(0, 10);
  const endRaw = String(endYmd ?? '').slice(0, 10);

  if (!startRaw && !endRaw) {
    return null;
  }
  if (!startRaw || !endRaw) {
    return formatPlanDateCompact(startRaw || endRaw);
  }

  const startDate = parseYmd(startRaw);
  const endDate = parseYmd(endRaw);
  if (!startDate || !endDate) {
    return null;
  }

  if (startDate.getFullYear() !== endDate.getFullYear()) {
    return formatPlanPeriodRange(
      formatPlanDateWithYear(startRaw),
      formatPlanDateWithYear(endRaw),
    );
  }

  return `${formatPlanDateCompact(startRaw)} – ${formatPlanDateCompact(endRaw)}`;
}

export function resolveWeekSectionDateBounds(section) {
  let dateStart = '';
  let dateEnd = '';

  for (const header of section?.weekHeaders || []) {
    const start = String(header.dateStartSort ?? '').trim();
    const end = String(header.dateEndSort ?? header.dateStartSort ?? '').trim();

    if (start && (!dateStart || start < dateStart)) {
      dateStart = start;
    }
    if (end && (!dateEnd || end > dateEnd)) {
      dateEnd = end;
    }
  }

  return { dateStart, dateEnd };
}

export function formatPlanPeriod(summary, activeProgram) {
  const start = formatPlanDateWithYear(activeProgram?.date_start)
    || String(summary?.date_start ?? '').trim()
    || null;
  const end = formatPlanDateWithYear(activeProgram?.date_end_planned)
    || String(summary?.date_end_planned ?? '').trim()
    || null;

  return formatPlanPeriodRange(start, end);
}

function formatPlanPeriodRange(start, end) {
  if (start && end) {
    return `${start} – ${end}`;
  }
  if (start) {
    return start;
  }
  if (end) {
    return end;
  }
  return null;
}

export function formatPlanPeriodForProgram(program) {
  return formatCompactWeekDateRangeLabel(
    resolveProgramDateStartYmd(program),
    resolveProgramDateEndYmd(program),
  ) || null;
}

function parseLocalDate(str) {
  if (!str) {
    return null;
  }
  const [year, month, day] = String(str).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

function startOfToday(viewerTimezone) {
  const todayYmd = formatTodayYmd(viewerTimezone);
  return parseYmd(todayYmd) ?? new Date();
}

function formatTodayYmd(viewerTimezone) {
  return resolveTodayYmd(viewerTimezone ?? resolveDefaultTimezone());
}

function isSkipWeek(week) {
  if (!week) {
    return false;
  }
  if (week.is_skip_week) {
    return true;
  }
  return String(week.status ?? '').toLowerCase() === 'rest';
}

function isPlanWeekEntityDeleted(week) {
  return Boolean(week?.week_entity_deleted);
}

function resolveSkipWeekSectionTitle(week, weeksTotal) {
  const weekIndex = Number(week?.week_index) || 0;
  if (!weekIndex) {
    return 'Пауза без тренировок';
  }
  return weeksTotal
    ? `Неделя ${weekIndex} из ${weeksTotal} · Пауза`
    : `Неделя ${weekIndex} · Пауза`;
}

function isGenericProgramWeekTitle(title) {
  return /^неделя\s+\d+(\s+из\s+\d+)?(\s*[·\-–—].*)?$/iu.test(String(title ?? '').trim());
}

function resolveProgramFocusWeekDisplayName(week, weekIndex, {
  isViewedWeek,
  isSkipWeek,
  weeksTotal,
  summary,
  activeProgram,
  isFocusedProgram,
}) {
  if (isSkipWeek) {
    return resolveSkipWeekSectionTitle(week, weeksTotal);
  }

  const explicitTitle = String(week?.title ?? week?.week_title ?? week?.label ?? '').trim();
  if (explicitTitle && !isGenericProgramWeekTitle(explicitTitle)) {
    return explicitTitle;
  }

  if (isViewedWeek) {
    const viewedTitle = resolveCurrentWeekTitle({
      summary: isFocusedProgram ? summary : null,
      activeProgram: isFocusedProgram ? activeProgram : null,
      activeWeek: week,
    });
    if (viewedTitle && viewedTitle !== '—') {
      return viewedTitle;
    }
  }

  return `Неделя ${weekIndex}`;
}

const PROGRAM_PERIOD_DISPLAY_ORDER = {
  past: 0,
  active: 1,
  undated: 2,
  future: 3,
};

export function resolveClientTrainingPlanProgramPeriod(program, viewerTimezone) {
  const hasCalendarDates = programHasCalendarDatesForProgram(program);
  if (!hasCalendarDates) {
    return {
      kind: 'undated',
      eyebrowLabel: 'Будущая программа',
      periodLabel: 'даты не определены',
    };
  }

  const periodLabel = formatPlanPeriodForProgram(program);
  const today = startOfToday(viewerTimezone);
  const start = parseLocalDate(program?.date_start);
  const end = parseLocalDate(program?.date_end);
  const lifecycle = String(program?.lifecycle_status ?? '').toLowerCase();
  const status = String(program?.status ?? '').toLowerCase();

  if (status === 'completed' || lifecycle === 'completed' || lifecycle === 'archived') {
    return {
      kind: 'past',
      eyebrowLabel: 'Прошедшая программа',
      periodLabel,
    };
  }
  if (end && end < today) {
    return {
      kind: 'past',
      eyebrowLabel: 'Прошедшая программа',
      periodLabel,
    };
  }
  if (start && start > today) {
    return {
      kind: 'future',
      eyebrowLabel: 'Будущая программа',
      periodLabel,
    };
  }

  return {
    kind: 'active',
    eyebrowLabel: 'Активная программа',
    periodLabel,
  };
}

function formatWeekWorkoutsSectionEyebrowLabel(weekLabel) {
  const label = String(weekLabel || '').trim();
  if (!label || label === 'Неделя') {
    return 'Тренировки';
  }
  if (label === 'Текущая неделя') {
    return 'Тренировки текущей недели';
  }
  if (label === 'Прошедшая неделя') {
    return 'Тренировки прошедшей недели';
  }
  if (label === 'Следующая неделя') {
    return 'Тренировки следующей недели';
  }
  if (label.startsWith('Неделя ')) {
    return `Тренировки ${label.slice('Неделя '.length)}`;
  }
  return `Тренировки ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

function resolveSelectedWeekSectionEyebrowLabel(bounds) {
  const tabId = resolveActiveRelativeIsoWeekTabId(
    bounds,
    resolveRelativeIsoWeekNavBounds(),
  );

  if (tabId === 'past') {
    return formatWeekWorkoutsSectionEyebrowLabel('Прошедшая неделя');
  }
  if (tabId === 'active') {
    return formatWeekWorkoutsSectionEyebrowLabel('Текущая неделя');
  }
  if (tabId === 'next') {
    return formatWeekWorkoutsSectionEyebrowLabel('Следующая неделя');
  }

  const weekOffset = countIsoWeekOffset(
    resolveRelativeIsoWeekNavBounds().active,
    bounds,
  );
  const relativeLabel = formatRelativeIsoWeeksOffsetLabel(weekOffset);
  if (relativeLabel) {
    return formatWeekWorkoutsSectionEyebrowLabel(relativeLabel);
  }

  const period = formatWeekPeriodCompact(bounds?.dateStart, bounds?.dateEnd);
  return formatWeekWorkoutsSectionEyebrowLabel(period ? `Неделя ${period}` : 'Неделя');
}

function dateRangesOverlap(startA, endA, startB, endB) {
  return Boolean(
    startA && endA && startB && endB
    && startA <= endB
    && endA >= startB,
  );
}

function workoutOverlapsWeekBounds(workout, bounds) {
  if (!bounds?.dateStart || !bounds?.dateEnd) {
    return false;
  }

  const dateSort = String(workout?.date_sort ?? '').trim();
  if (dateSort) {
    return dateSort >= bounds.dateStart && dateSort <= bounds.dateEnd;
  }

  const scheduleContext = workout?.schedule_context;
  const isoYear = Number(scheduleContext?.iso_year) || 0;
  const isoWeek = Number(scheduleContext?.iso_week) || 0;
  if (!isoYear || !isoWeek || !bounds.isoYear || !bounds.isoWeek) {
    return false;
  }

  return isoYear === bounds.isoYear && isoWeek === bounds.isoWeek;
}

function resolveWeekDateStartYmd(week) {
  return String(
    week?.date_start_raw
    ?? week?.date_start_sort
    ?? week?.date_start
    ?? '',
  ).trim().slice(0, 10);
}

function resolveWeekDateEndYmd(week) {
  return String(
    week?.date_end_raw
    ?? week?.date_end_sort
    ?? week?.date_end
    ?? '',
  ).trim().slice(0, 10);
}

function weekOverlapsBounds(week, bounds) {
  const start = resolveWeekDateStartYmd(week);
  const end = resolveWeekDateEndYmd(week);
  if (!start || !end || !bounds?.dateStart || !bounds?.dateEnd) {
    return false;
  }

  return dateRangesOverlap(start, end, bounds.dateStart, bounds.dateEnd);
}

function resolveProgramDateStartYmd(program) {
  return String(
    program?.date_start_raw
    ?? program?.date_start_sort
    ?? program?.date_start
    ?? '',
  ).trim().slice(0, 10);
}

function resolveProgramDateEndYmd(program) {
  return String(
    program?.date_end_raw
    ?? program?.date_end_sort
    ?? program?.date_end
    ?? program?.date_end_planned
    ?? '',
  ).trim().slice(0, 10);
}

function programSpanOverlapsWeekBounds(program, bounds) {
  const start = resolveProgramDateStartYmd(program);
  const end = resolveProgramDateEndYmd(program);
  if (!start || !end || !bounds?.dateStart || !bounds?.dateEnd) {
    return false;
  }

  return dateRangesOverlap(start, end, bounds.dateStart, bounds.dateEnd);
}

function programOverlapsWeekBounds(program, bounds) {
  if (!bounds || !programHasCalendarDatesForProgram(program)) {
    return false;
  }

  if (programSpanOverlapsWeekBounds(program, bounds)) {
    return true;
  }

  for (const phase of program?.phases || []) {
    for (const week of phase.weeks || []) {
      if (weekOverlapsBounds(week, bounds)) {
        return true;
      }
    }
  }

  return false;
}

function findProgramWeekInBounds(program, bounds) {
  for (const phase of program?.phases || []) {
    for (const week of phase.weeks || []) {
      if (weekOverlapsBounds(week, bounds)) {
        return week;
      }
    }
  }

  return null;
}

export function formatWorkoutScheduleParts(workout, { viewerTodayYmd = formatTodayYmd() } = {}) {
  const viewerDateYmd = String(workout?.schedule_context?.timezone_context?.viewer?.local_date ?? '').slice(0, 10);
  const dateSort = viewerDateYmd || String(workout?.date_sort ?? '').trim();
  const hasTime = hasWorkoutStartTime(workout);
  const time = resolveWorkoutScheduleTimeLabel(workout);

  if (workout?.schedule_context?.calendar_day_known === false) {
    const relativeDayLabel = formatWorkoutRelativeDayLabel(workout?.schedule_context);
    const weekLabel = String(workout?.date ?? '').trim()
      || String(workout?.schedule_context?.iso_week_label ?? '').trim();
    const hint = String(workout?.schedule_context?.schedule_day_hint ?? '').trim();
    const date = [relativeDayLabel, weekLabel, hint].filter(Boolean).join('. ')
      || WORKOUT_SCHEDULE_DATE_PLACEHOLDER;

    return {
      date,
      time,
      hasDate: false,
      hasTime,
    };
  }

  if (!dateSort) {
    const dateLabel = String(workout?.date ?? '').trim();
    if (dateLabel && dateLabel !== '—') {
      return {
        date: dateLabel,
        time,
        hasDate: false,
        hasTime,
      };
    }

    return {
      date: WORKOUT_SCHEDULE_DATE_PLACEHOLDER,
      time,
      hasDate: false,
      hasTime,
    };
  }

  const workoutDate = parseYmd(dateSort);
  if (!workoutDate) {
    const dateLabel = String(workout?.date ?? '').trim();
    return {
      date: dateLabel || WORKOUT_SCHEDULE_DATE_PLACEHOLDER,
      time,
      hasDate: Boolean(dateLabel),
      hasTime,
    };
  }

  const diffDays = diffCalendarDays(dateSort, viewerTodayYmd);

  if (diffDays === 0) {
    return { date: 'Сегодня', time, hasDate: true, hasTime };
  }
  if (diffDays === 1) {
    return { date: 'Завтра', time, hasDate: true, hasTime };
  }

  const weekday = WEEKDAY_SHORT[workoutDate.getDay()] || '';
  const month = SHORT_MONTHS[workoutDate.getMonth() + 1] || '';
  return {
    date: `${weekday}., ${workoutDate.getDate()} ${month}`,
    time,
    hasDate: true,
    hasTime,
  };
}

export function formatWorkoutScheduleLabel(workout) {
  const { date, time } = formatWorkoutScheduleParts(workout);
  return `${date} ${time}`;
}

export function resolveWorkoutPlanStatusLabel(workout) {
  const overview = resolveWorkoutPlanStatusView(workout);
  if (overview.badge?.label) {
    return `Черновик. ${overview.badge.label}`;
  }
  return overview.statusBadge?.label || overview.label || '—';
}

function resolveCurrentWeekTitle({ summary, activeProgram, activeWeek }) {
  const candidates = [
    activeWeek?.label,
    summary?.current_week_title,
    activeProgram?.current_week?.title,
  ];

  for (const candidate of candidates) {
    const title = String(candidate ?? '').trim();
    if (title && !/^неделя\s+\d+$/iu.test(title)) {
      return title;
    }
  }

  const fallback = candidates.map((candidate) => String(candidate ?? '').trim()).find(Boolean);
  return fallback || '—';
}

function programHasCalendarDatesForProgram(program) {
  if (program?.has_calendar_dates != null) {
    return Boolean(program.has_calendar_dates);
  }

  return Boolean(
    formatPlanDateWithYear(program?.date_start)
    || formatPlanDateWithYear(program?.date_end),
  );
}

function programMatchesActiveHint(program, activeProgramHint) {
  if (!program) {
    return false;
  }

  const activeUuid = String(
    activeProgramHint?.program_uuid
    ?? activeProgramHint?.id
    ?? '',
  ).trim();

  if (activeUuid) {
    return String(program.id ?? '').trim() === activeUuid
      || String(program.program_uuid ?? '').trim() === activeUuid;
  }

  return Boolean(program.is_active);
}

function collectAllPrograms(entries) {
  const programs = [];
  const seen = new Set();

  function pushProgram(program) {
    const id = String(program?.id ?? program?.program_uuid ?? '').trim();
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    programs.push(program);
  }

  for (const entry of entries) {
    if (entry.kind === 'program' && entry.program) {
      pushProgram(entry.program);
    }
    if (entry.kind === 'undated_section') {
      for (const program of entry.programs || []) {
        pushProgram(program);
      }
    }
  }

  return programs;
}

function compareProgramsChronologically(leftProgram, rightProgram) {
  const leftSort = String(leftProgram?.date_start_sort ?? leftProgram?.date_start ?? '').trim();
  const rightSort = String(rightProgram?.date_start_sort ?? rightProgram?.date_start ?? '').trim();

  if (leftSort && rightSort) {
    return leftSort.localeCompare(rightSort);
  }
  if (leftSort) {
    return -1;
  }
  if (rightSort) {
    return 1;
  }

  return String(leftProgram?.title ?? '').localeCompare(String(rightProgram?.title ?? ''), 'ru');
}

function sortProgramsForDisplay(programs) {
  return programs
    .map((program) => ({
      program,
      period: resolveClientTrainingPlanProgramPeriod(program),
    }))
    .sort((left, right) => {
      const orderLeft = PROGRAM_PERIOD_DISPLAY_ORDER[left.period.kind] ?? 9;
      const orderRight = PROGRAM_PERIOD_DISPLAY_ORDER[right.period.kind] ?? 9;
      if (orderLeft !== orderRight) {
        return orderLeft - orderRight;
      }
      return compareProgramsChronologically(left.program, right.program);
    })
    .map(({ program }) => program);
}

function countProgramWeeksInPhases(program) {
  return (program?.phases || []).reduce(
    (total, phase) => total + (phase.weeks?.length || 0),
    0,
  );
}

function countProgramWeeksFromPhaseMeta(program) {
  const fromPhaseMeta = (program?.phases || []).reduce(
    (total, phase) => total + (Number(phase.weeks_count) || 0),
    0,
  );
  return fromPhaseMeta > 0 ? fromPhaseMeta : 0;
}

function resolveProgramWeeksTotal(program, { summary, activeProgram, isFocusedProgram }) {
  if (isFocusedProgram) {
    const fromSummary = Number(summary?.weeks_total) || 0;
    if (fromSummary > 0) {
      return fromSummary;
    }
  }

  const fromProgramFields = Number(program?.weeks_total ?? program?.duration_weeks) || 0;
  if (fromProgramFields > 0) {
    return fromProgramFields;
  }

  const programUuid = String(program?.program_uuid ?? program?.id ?? '').trim();
  const activeProgramUuid = String(activeProgram?.program_uuid ?? activeProgram?.id ?? '').trim();
  if (programUuid && programUuid === activeProgramUuid) {
    const fromActiveProgram = Number(activeProgram?.weeks_total ?? activeProgram?.duration_weeks) || 0;
    if (fromActiveProgram > 0) {
      return fromActiveProgram;
    }
  }

  const fromPhaseMeta = countProgramWeeksFromPhaseMeta(program);
  if (fromPhaseMeta > 0) {
    return fromPhaseMeta;
  }

  return countProgramWeeksInPhases(program);
}

function countProgramPhases(program) {
  return program?.phases?.length || 0;
}

function formatIndexOfLabel(index, total) {
  const normalizedIndex = Number(index) || 0;
  const normalizedTotal = Number(total) || 0;
  if (!normalizedIndex) {
    return null;
  }
  if (!normalizedTotal) {
    return String(normalizedIndex);
  }
  return `${normalizedIndex} из ${normalizedTotal}`;
}

function resolveCurrentPhaseIndex(program, { summary, activeProgram, isFocusedProgram }) {
  const phases = program?.phases || [];
  if (!phases.length) {
    return 0;
  }

  if (isFocusedProgram) {
    const fromSummary = Number(summary?.current_stage_index);
    if (fromSummary > 0) {
      return fromSummary;
    }
    const fromActiveProgram = Number(activeProgram?.current_stage_index);
    if (fromActiveProgram > 0) {
      return fromActiveProgram;
    }
  }

  const activePhaseIndex = phases.findIndex((phase) => phase.is_active);
  if (activePhaseIndex >= 0) {
    return activePhaseIndex + 1;
  }

  return 1;
}

function resolveCurrentPhaseTitle(program, { summary, activeProgram, isFocusedProgram }) {
  const activePhase = program?.phases?.find((phase) => phase.is_active) || program?.phases?.[0];
  if (isFocusedProgram) {
    return summary?.current_phase_title
      || activeProgram?.current_stage_title
      || activePhase?.title
      || '—';
  }
  return activePhase?.title || '—';
}

function resolveProgramFocusContext(program, { summary, activeProgram }) {
  const isFocusedProgram = programMatchesActiveHint(program, activeProgram)
    || (
      String(summary?.active_program_uuid ?? '').trim()
      && String(summary.active_program_uuid).trim() === String(program?.id ?? program?.program_uuid ?? '').trim()
    );
  const currentWeekIndex = isFocusedProgram
    ? (summary?.current_week_index ?? program?.current_week_index)
    : program?.current_week_index;
  const hasCalendarDates = programHasCalendarDatesForProgram(program);
  const weeksTotal = resolveProgramWeeksTotal(program, {
    summary,
    activeProgram,
    isFocusedProgram,
  });

  return {
    isFocusedProgram,
    currentWeekIndex,
    hasCalendarDates,
    weeksTotal,
  };
}

function formatPhaseDisplayTitle(phase) {
  return String(phase?.title ?? phase?.name ?? '').trim() || '—';
}

function resolvePhaseByWeekIndex(program, weekIndex) {
  const index = Number(weekIndex) || 0;
  if (index <= 0) {
    return null;
  }

  const phases = program?.phases || [];
  let nextStartIndex = 1;

  for (const phase of phases) {
    const weeksCountFromMeta = Number(phase.weeks_count) || 0;
    const visibleWeekCount = Array.isArray(phase.weeks) ? phase.weeks.length : 0;
    const weeksInPhase = weeksCountFromMeta > 0 ? weeksCountFromMeta : visibleWeekCount;

    if (weeksInPhase <= 0) {
      continue;
    }

    const phaseEndIndex = nextStartIndex + weeksInPhase - 1;
    if (index >= nextStartIndex && index <= phaseEndIndex) {
      return phase;
    }

    nextStartIndex += weeksInPhase;
  }

  return null;
}

function findPhaseIndexForWeek(program, week) {
  if (!week) {
    return 0;
  }

  const phases = program?.phases || [];
  for (let index = 0; index < phases.length; index += 1) {
    for (const candidate of phases[index].weeks || []) {
      if (candidate === week) {
        return index + 1;
      }
      if (week.id && candidate.id === week.id) {
        return index + 1;
      }
      if (Number(week.week_index) && Number(candidate.week_index) === Number(week.week_index)) {
        return index + 1;
      }
    }
  }

  const weekIndex = Number(week.week_index) || 0;
  if (weekIndex > 0) {
    const phase = resolvePhaseByWeekIndex(program, weekIndex);
    if (phase) {
      const phaseIndex = phases.indexOf(phase);
      if (phaseIndex >= 0) {
        return phaseIndex + 1;
      }
    }
  }

  return 0;
}

function resolvePhaseTitleForWeek(program, week) {
  const phases = program?.phases || [];
  for (const phase of phases) {
    for (const candidate of phase.weeks || []) {
      if (candidate === week
        || (week?.id && candidate.id === week.id)
        || (Number(week?.week_index) && Number(candidate.week_index) === Number(week.week_index))) {
        return formatPhaseDisplayTitle(phase);
      }
    }
  }

  const weekIndex = Number(week?.week_index) || 0;
  if (weekIndex > 0) {
    const phase = resolvePhaseByWeekIndex(program, weekIndex);
    if (phase) {
      return formatPhaseDisplayTitle(phase);
    }
  }

  return '—';
}

function buildActiveProgramCardViewModel(program, context, { programIndex, showProgramIndex, selectedWeekBounds, viewerTimezone }) {
  const period = resolveClientTrainingPlanProgramPeriod(program, viewerTimezone);
  const {
    isFocusedProgram,
    currentWeekIndex,
    hasCalendarDates,
    weeksTotal,
  } = context;
  const progress = countProgramProgress(program);
  const activeWeek = selectedWeekBounds
    ? findProgramWeekInBounds(program, selectedWeekBounds)
    : findActiveWeek(program, currentWeekIndex, hasCalendarDates, viewerTimezone);

  let displayWeekIndex = currentWeekIndex;
  if (activeWeek) {
    displayWeekIndex = Number(activeWeek.week_index) || displayWeekIndex;
  }

  const phasesTotal = isFocusedProgram
    ? (countProgramPhases(program) || Number(context.activeProgram?.stages?.length) || 0)
    : countProgramPhases(program);
  const currentPhaseIndex = activeWeek
    ? findPhaseIndexForWeek(program, activeWeek)
    : resolveCurrentPhaseIndex(program, {
      summary: context.summary,
      activeProgram: context.activeProgram,
      isFocusedProgram,
    });
  const weekTitleContext = {
    summary: context.summary,
    activeProgram: context.activeProgram,
    activeWeek,
  };

  return {
    id: program.id || program.program_uuid,
    programIndex,
    showProgramIndex,
    eyebrowLabel: period.eyebrowLabel,
    periodLabel: period.periodLabel,
    programTitle: String(program?.title ?? '').trim() || '—',
    programOpenUrl: program?.open_url
      || program?.program_detail_url
      || (program?.program_uuid ? `/training-programs/${program.program_uuid}` : null),
    phaseStatLabel: 'Текущая фаза',
    weekStatLabel: 'Текущая неделя',
    currentPhaseTitle: activeWeek
      ? resolvePhaseTitleForWeek(program, activeWeek)
      : resolveCurrentPhaseTitle(program, {
        summary: context.summary,
        activeProgram: context.activeProgram,
        isFocusedProgram,
      }),
    currentPhaseIndexLabel: formatIndexOfLabel(currentPhaseIndex, phasesTotal),
    currentWeekTitle: resolveCurrentWeekTitle(weekTitleContext),
    currentWeekIndexLabel: formatIndexOfLabel(displayWeekIndex, weeksTotal),
    workoutProgressValue: progress.total ? progress.completed : null,
    workoutProgressIndexLabel: progress.total ? `из ${progress.total}` : null,
    hasCalendarDates,
  };
}

const ADDITIONAL_WORKOUTS_WEEK_GROUP_ID = 'additional-workouts';

function mergeWeekHeaderWorkouts(weekHeaders, { showProgramIndex, showWeekGrouping }) {
  const mergedById = new Map();

  for (const weekHeader of weekHeaders) {
    for (const workout of weekHeader.workouts || []) {
      if (mergedById.has(workout.id)) {
        continue;
      }

      mergedById.set(workout.id, {
        ...workout,
        programIndex: weekHeader.programIndex,
        showProgramIndex,
        weekGroupId: showWeekGrouping ? weekHeader.id : null,
        weekGroupDisplayIndex: showWeekGrouping ? weekHeader.displayIndex : null,
      });
    }
  }

  return [...mergedById.values()].sort(sortWorkoutsChronologically);
}

function buildAdditionalWorkoutsHeader(orphanWorkouts, displayIndex) {
  return {
    id: ADDITIONAL_WORKOUTS_WEEK_GROUP_ID,
    weekGroupId: ADDITIONAL_WORKOUTS_WEEK_GROUP_ID,
    title: 'Дополнительные тренировки',
    isAdditionalWorkouts: true,
    openUrl: null,
    displayIndex,
    workouts: orphanWorkouts,
  };
}

function buildSelectedWeekBlocks(weekHeaders, orphanWorkouts) {
  const hasOrphanWorkouts = orphanWorkouts.length > 0;
  const totalBlocks = weekHeaders.length + (hasOrphanWorkouts ? 1 : 0);
  const showBlockIndex = totalBlocks > 1;
  const weekBlocks = [];

  for (const [index, weekHeader] of weekHeaders.entries()) {
    weekHeader.displayIndex = showBlockIndex ? index + 1 : null;
    weekHeader.weekGroupId = weekHeader.id;

    weekBlocks.push({
      id: weekHeader.id,
      weekGroupId: weekHeader.id,
      title: weekHeader.title,
      isAdditionalWorkouts: false,
      isSkipWeek: weekHeader.isSkipWeek,
      openUrl: weekHeader.openUrl,
      load: weekHeader.load,
      role: weekHeader.role,
      displayIndex: weekHeader.displayIndex,
      workouts: weekHeader.workouts || [],
      weekStartYmd: weekHeader.dateStartSort,
      weekHeader,
    });
  }

  let additionalWorkoutsHeader = null;
  if (hasOrphanWorkouts) {
    additionalWorkoutsHeader = buildAdditionalWorkoutsHeader(
      orphanWorkouts,
      showBlockIndex ? weekBlocks.length + 1 : null,
    );
    weekBlocks.push({
      id: additionalWorkoutsHeader.id,
      weekGroupId: additionalWorkoutsHeader.weekGroupId,
      title: additionalWorkoutsHeader.title,
      isAdditionalWorkouts: true,
      isSkipWeek: false,
      openUrl: null,
      load: '',
      role: null,
      displayIndex: additionalWorkoutsHeader.displayIndex,
      workouts: orphanWorkouts,
      weekStartYmd: '',
      weekHeader: additionalWorkoutsHeader,
    });
  }

  return {
    weekBlocks,
    showWeekGrouping: showBlockIndex,
    showAdditionalWorkoutsHeader: hasOrphanWorkouts && weekHeaders.length > 0,
    groupHeaders: weekBlocks.map((block) => block.weekHeader),
    additionalWorkoutsHeader,
  };
}

function buildSelectedWeekSection(entries, weekBounds, {
  summary,
  activeProgram,
  programsForWeek,
  viewerTodayYmd = formatTodayYmd(),
}) {
  if (!weekBounds?.dateStart || !weekBounds?.dateEnd) {
    return null;
  }

  const allDatedWorkouts = collectAllDatedWorkouts(entries);
  const weekHeaders = [];
  const showProgramIndex = programsForWeek.length > 1;

  for (const [index, program] of programsForWeek.entries()) {
    const context = resolveProgramFocusContext(program, { summary, activeProgram });
    const { hasCalendarDates, weeksTotal, isFocusedProgram } = context;

    for (const phase of program?.phases || []) {
      for (const week of phase.weeks || []) {
        if (!weekOverlapsBounds(week, weekBounds)) {
          continue;
        }
        if (isPlanWeekEntityDeleted(week)) {
          continue;
        }

        weekHeaders.push(buildWeekEntryViewModel(program, week, {
          hasCalendarDates,
          isFocusedProgram,
          summary,
          activeProgram,
          weeksTotal,
          programIndex: index + 1,
          showProgramIndex,
          allDatedWorkouts,
          viewerTodayYmd,
        }));
      }
    }
  }

  for (const week of collectOrphanWeeksFromEntries(entries)) {
    if (!weekOverlapsBounds(week, weekBounds)) {
      continue;
    }

    weekHeaders.push(buildWeekEntryViewModel(null, week, {
      hasCalendarDates: true,
      isFocusedProgram: false,
      summary: null,
      activeProgram: null,
      weeksTotal: 0,
      programIndex: 0,
      showProgramIndex: false,
      allDatedWorkouts,
      viewerTodayYmd,
    }));
  }

  const orphanWorkouts = collectOrphanWorkoutsFromEntries(entries)
    .filter((workout) => workoutOverlapsWeekBounds(workout, weekBounds))
    .map((workout) => ({
      ...workout,
      schedule: formatWorkoutScheduleParts(workout, { viewerTodayYmd }),
    }));

  weekHeaders.sort((left, right) => (
    String(left.dateStartSort ?? '').localeCompare(String(right.dateStartSort ?? ''))
  ));

  for (const weekHeader of weekHeaders) {
    weekHeader.showProgramIndex = showProgramIndex;
  }

  const {
    weekBlocks,
    showWeekGrouping,
    showAdditionalWorkoutsHeader,
    groupHeaders,
    additionalWorkoutsHeader,
  } = buildSelectedWeekBlocks(weekHeaders, orphanWorkouts);

  const workoutsFromWeeks = mergeWeekHeaderWorkouts(weekHeaders, {
    showProgramIndex,
    showWeekGrouping,
  });
  const workoutsById = new Map();

  for (const workout of workoutsFromWeeks) {
    workoutsById.set(workout.id, workout);
  }
  for (const workout of orphanWorkouts) {
    if (!workoutsById.has(workout.id)) {
      workoutsById.set(workout.id, {
        ...workout,
        weekGroupId: showAdditionalWorkoutsHeader ? ADDITIONAL_WORKOUTS_WEEK_GROUP_ID : null,
        weekGroupDisplayIndex: showAdditionalWorkoutsHeader
          ? additionalWorkoutsHeader.displayIndex
          : null,
      });
    }
  }

  const workouts = [...workoutsById.values()].sort(sortWorkoutsChronologically);
  const realWorkoutCount = workouts.filter(isWorkoutRealPlanEntity).length;
  const section = {
    id: `${weekBounds.isoYear}-${weekBounds.isoWeek}`,
    eyebrowLabel: resolveSelectedWeekSectionEyebrowLabel(weekBounds),
    weekHeaders,
    weekBlocks,
    groupHeaders,
    showWeekGrouping,
    showAdditionalWorkoutsHeader,
    additionalWorkoutsHeader,
    workouts,
    realWorkoutCount,
    hasRealWorkouts: realWorkoutCount > 0,
    isAllSkipWeeks: weekHeaders.length > 0
      && weekHeaders.every((weekHeader) => weekHeader.isSkipWeek)
      && !workouts.length,
    showWeekActions: weekHeaders.length > 0 || workouts.length > 0 || realWorkoutCount === 0,
    firstWeekOpenUrl: weekHeaders.find((weekHeader) => weekHeader.openUrl)?.openUrl ?? null,
  };

  return section;
}

function sortWorkoutsChronologically(left, right) {
  const leftRelativeDay = resolveWorkoutRelativeDayNumber(left?.schedule_context);
  const rightRelativeDay = resolveWorkoutRelativeDayNumber(right?.schedule_context);
  if (leftRelativeDay !== null || rightRelativeDay !== null) {
    if (leftRelativeDay === null) return 1;
    if (rightRelativeDay === null) return -1;
    if (leftRelativeDay !== rightRelativeDay) {
      return leftRelativeDay - rightRelativeDay;
    }
  }

  const dateCompare = String(left.date_sort ?? '').localeCompare(String(right.date_sort ?? ''));
  if (dateCompare !== 0) {
    return dateCompare;
  }
  return String(left.start_time ?? '').localeCompare(String(right.start_time ?? ''));
}

function isWorkoutUndatedPlanDay(workout) {
  if (isWorkoutCalendarDayKnown(workout?.schedule_context)) {
    return false;
  }
  if (resolveWorkoutRelativeDayNumber(workout?.schedule_context) !== null) {
    return true;
  }
  return isWorkoutFullyUndeterminedScheduleDay(workout?.schedule_context);
}

function mapWeekWorkouts(week, hasCalendarDates, viewerTodayYmd = formatTodayYmd()) {
  return (week?.workouts || [])
    .filter((workout) => (
      isWorkoutMissingPlanSlot(workout)
        ? true
        : hasCalendarDates
          ? Boolean(String(workout?.date_sort ?? '').trim()) || isWorkoutUndatedPlanDay(workout)
          : true
    ))
    .slice()
    .sort(sortWorkoutsChronologically)
    .map((workout) => ({
      ...workout,
      schedule: formatWorkoutScheduleParts(workout, { viewerTodayYmd }),
    }));
}

function collectAllDatedWorkouts(entries) {
  const workouts = [];
  const seen = new Set();

  function pushWorkout(workout) {
    const id = workout?.id;
    if (!id || seen.has(id)) {
      return;
    }
    if (!String(workout?.date_sort ?? '').trim()) {
      return;
    }
    seen.add(id);
    workouts.push(workout);
  }

  for (const entry of entries || []) {
    if (entry.kind === 'program' && entry.program) {
      for (const workout of collectProgramWorkouts(entry.program)) {
        pushWorkout(workout);
      }
    }
    if (entry.kind === 'orphan_week' && entry.week) {
      for (const workout of entry.week.workouts || []) {
        pushWorkout(workout);
      }
    }
    if (entry.kind === 'orphan_workouts') {
      for (const workout of entry.workouts || []) {
        pushWorkout(workout);
      }
    }
    if (entry.kind === 'undated_section') {
      for (const program of entry.programs || []) {
        for (const workout of collectProgramWorkouts(program)) {
          pushWorkout(workout);
        }
      }
      for (const week of entry.weeks || []) {
        for (const workout of week.workouts || []) {
          pushWorkout(workout);
        }
      }
      for (const workout of entry.workouts || []) {
        pushWorkout(workout);
      }
    }
  }

  return workouts;
}

function resolveWorkoutPlanWeekUuid(workout) {
  return String(
    workout?.schedule_context?.week_uuid
      ?? workout?.week_uuid
      ?? '',
  ).trim();
}

function workoutBelongsToPlanWeek(workout, week) {
  if (!workout || !week) {
    return false;
  }

  const weekEntityUuid = resolveWeekEntityUuidFromPlanWeek(week);
  const workoutWeekUuid = resolveWorkoutPlanWeekUuid(workout);
  if (weekEntityUuid && workoutWeekUuid && weekEntityUuid === workoutWeekUuid) {
    return true;
  }

  for (const weekWorkout of week?.workouts || []) {
    if (weekWorkout?.id && weekWorkout.id === workout?.id) {
      return true;
    }
  }

  return false;
}

function resolveWorkoutsForWeek(week, allDatedWorkouts, hasCalendarDates, viewerTodayYmd = formatTodayYmd()) {
  const start = String(week?.date_start_raw ?? '').trim();
  const end = String(week?.date_end_raw ?? '').trim();
  const weekWorkouts = mapWeekWorkouts(week, hasCalendarDates, viewerTodayYmd);

  if (start && end) {
    const seen = new Set();
    const merged = [];

    for (const workout of weekWorkouts) {
      const dateSort = String(workout?.date_sort ?? '').trim();
      if (dateSort && (dateSort < start || dateSort > end)) {
        continue;
      }
      if (seen.has(workout.id)) {
        continue;
      }
      seen.add(workout.id);
      merged.push(workout);
    }

    for (const workout of allDatedWorkouts) {
      if (seen.has(workout.id)) {
        continue;
      }
      if (!workoutBelongsToPlanWeek(workout, week)) {
        continue;
      }
      const dateSort = String(workout?.date_sort ?? '').trim();
      if (!dateSort || dateSort < start || dateSort > end) {
        continue;
      }
      seen.add(workout.id);
      merged.push({
        ...workout,
        schedule: formatWorkoutScheduleParts(workout, { viewerTodayYmd }),
      });
    }

    return merged.sort(sortWorkoutsChronologically);
  }

  return weekWorkouts;
}

function buildWeekEntryViewModel(program, week, {
  hasCalendarDates,
  isFocusedProgram,
  summary,
  activeProgram,
  weeksTotal = 0,
  programIndex = 0,
  showProgramIndex = false,
  showWeekIndex = false,
  allDatedWorkouts = [],
  viewerTodayYmd = formatTodayYmd(),
}) {
  const programId = program?.id || program?.program_uuid || 'orphan';
  const weekKey = week?.id || week?.week_index || 'week';
  const skipWeek = isSkipWeek(week);
  const weekIndex = Number(week?.week_index) || 0;
  const mappedWorkouts = resolveWorkoutsForWeek(week, allDatedWorkouts, hasCalendarDates, viewerTodayYmd);

  return {
    id: `${programId}:${weekKey}`,
    programIndex,
    showProgramIndex,
    weekIndex,
    showWeekIndex,
    weekIndexBadge: showWeekIndex && weekIndex ? weekIndex : null,
    weekIndexLabel: formatIndexOfLabel(weekIndex, weeksTotal),
    dateStartSort: String(week?.date_start_raw ?? week?.date_start_sort ?? '').trim(),
    dateEndSort: String(week?.date_end_raw ?? week?.date_end_sort ?? '').trim(),
    load: week?.load ?? '',
    role: week?.role ?? null,
    title: skipWeek
      ? resolveSkipWeekSectionTitle(week, weeksTotal)
      : resolveCurrentWeekTitle({
        summary: isFocusedProgram ? summary : null,
        activeProgram: isFocusedProgram ? activeProgram : null,
        activeWeek: week,
      }),
    isSkipWeek: skipWeek,
    dateLabel: skipWeek && week?.date_start && week?.date_end
      ? `${week.date_start} – ${week.date_end}`
      : null,
    openUrl: getWeekOpenHref(week, program?.program_uuid || program?.id)
      || week?.open_url
      || null,
    workouts: mappedWorkouts,
    fillContext: buildWeekFillContext(program, week, mappedWorkouts),
    programUuid: String(program?.program_uuid ?? program?.id ?? '').trim() || null,
  };
}

function collectOrphanWeeksFromEntries(entries) {
  const weeks = [];

  for (const entry of entries || []) {
    if (entry.kind === 'orphan_week' && entry.week) {
      weeks.push(entry.week);
    }
  }

  return weeks;
}

function collectOrphanWorkoutsFromEntries(entries) {
  const workouts = [];

  for (const entry of entries || []) {
    if (entry.kind === 'orphan_workouts') {
      for (const workout of entry.workouts || []) {
        workouts.push(workout);
      }
    }
  }

  return workouts;
}

function collectNonProgramWorkoutsInWeekBounds(entries, weekBounds) {
  const workouts = [];
  const seen = new Set();

  function pushWorkout(workout) {
    const id = workout?.id;
    if (!id || seen.has(id)) {
      return;
    }
    if (!workoutOverlapsWeekBounds(workout, weekBounds)) {
      return;
    }
    seen.add(id);
    workouts.push(workout);
  }

  for (const entry of entries || []) {
    if (entry.kind === 'orphan_week' && entry.week) {
      if (!weekOverlapsBounds(entry.week, weekBounds)) {
        continue;
      }
      for (const workout of entry.week.workouts || []) {
        pushWorkout(workout);
      }
      continue;
    }

    if (entry.kind === 'orphan_workouts') {
      for (const workout of entry.workouts || []) {
        pushWorkout(workout);
      }
    }
  }

  return workouts.sort(sortWorkoutsChronologically);
}

export function buildClientTrainingPlanOutsideProgramsFocusViewModel(workouts, weekBounds, viewerTimezone) {
  const viewerTodayYmd = formatTodayYmd(viewerTimezone);
  const plannedTotal = workouts.length;
  const completedTotal = workouts.filter((workout) => workout.status === 'completed').length;
  const calendarState = resolveProgramFocusWeekCalendarState(
    {
      date_start_raw: weekBounds?.dateStart,
      date_end_raw: weekBounds?.dateEnd,
    },
    viewerTodayYmd,
  );

  return {
    title: 'Тренировки вне программ',
    workoutsPlannedTotal: plannedTotal,
    workoutsCompletedTotal: completedTotal,
    weekStartDateLabel: formatPlanDateCompact(weekBounds?.dateStart) || '—',
    weekEndDateLabel: formatPlanDateCompact(weekBounds?.dateEnd) || '—',
    calendarState,
  };
}

function findCalendarWeekContainingToday(program, viewerTimezone) {
  const today = formatTodayYmd(viewerTimezone);

  for (const phase of program?.phases || []) {
    for (const week of phase.weeks || []) {
      const start = String(week.date_start_raw ?? '').trim();
      const end = String(week.date_end_raw ?? '').trim();
      if (!start || !end) {
        continue;
      }
      if (start <= today && today <= end) {
        return week;
      }
    }
  }

  return null;
}

function findActiveWeek(program, currentWeekIndex, hasCalendarDates = false, viewerTimezone) {
  if (!program?.phases?.length) {
    return null;
  }

  if (hasCalendarDates) {
    return findCalendarWeekContainingToday(program, viewerTimezone);
  }

  for (const phase of program.phases) {
    for (const week of phase.weeks || []) {
      if (week.is_active) {
        return week;
      }
    }
  }

  const index = Number(currentWeekIndex) || 0;
  if (!index) {
    return null;
  }

  for (const phase of program.phases) {
    for (const week of phase.weeks || []) {
      if (Number(week.week_index) === index) {
        return week;
      }
    }
  }

  return program.phases[0]?.weeks?.[0] || null;
}

function collectProgramWorkouts(program) {
  const workouts = [];
  for (const phase of program?.phases || []) {
    for (const week of phase.weeks || []) {
      for (const workout of week.workouts || []) {
        workouts.push(workout);
      }
    }
  }
  return workouts;
}

function collectProgramWorkoutIds(program) {
  const ids = new Set();
  for (const workout of collectProgramWorkouts(program)) {
    const id = workout?.id;
    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

function findUndatedSection(entries) {
  return (entries || []).find((entry) => entry.kind === 'undated_section') || null;
}

function collectUndatedPrograms(entries) {
  return findUndatedSection(entries)?.programs || [];
}

function collectUndatedWeeks(entries) {
  return findUndatedSection(entries)?.weeks || [];
}

function pluralizeRu(count, forms) {
  const abs = Math.abs(Number(count) || 0);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 19) {
    return forms[2];
  }
  if (mod10 === 1) {
    return forms[0];
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return forms[1];
  }
  return forms[2];
}

function pluralizeWithoutDatesLabel(count) {
  return pluralizeRu(count, ['без даты', 'без дат', 'без дат']);
}

export function resolveUndatedProgramHref(program) {
  const openUrl = String(program?.open_url ?? program?.program_detail_url ?? '').trim();
  if (openUrl) {
    return openUrl;
  }

  const programUuid = String(program?.program_uuid ?? program?.id ?? '').trim();
  return programUuid ? `/training-programs/${programUuid}` : null;
}

export function resolveUndatedWeekHref(week) {
  const openUrl = String(week?.open_url ?? '').trim();
  if (openUrl) {
    return openUrl;
  }

  return getWeekOpenHref(week, week?.parent_uuid || week?.program_uuid) || null;
}

function resolveUndatedEntityHref(kind, entities) {
  if (entities.length !== 1) {
    return null;
  }

  const entity = entities[0];
  if (kind === 'programs') {
    return resolveUndatedProgramHref(entity);
  }
  if (kind === 'weeks') {
    return resolveUndatedWeekHref(entity);
  }
  if (kind === 'workouts') {
    return resolveWorkoutPlanRowHref(entity);
  }

  return null;
}

export function buildUndatedPlanWarnings({
  undatedPrograms = [],
  undatedWeeks = [],
  undatedWorkouts = [],
  undatedCounts = null,
} = {}) {
  const warnings = [];

  const programsCount = undatedPrograms.length || Number(undatedCounts?.programs) || 0;
  const weeksCount = undatedWeeks.length || Number(undatedCounts?.weeks) || 0;
  const workoutsCount = undatedWorkouts.length || Number(undatedCounts?.workouts) || 0;

  if (programsCount) {
    const count = programsCount;
    warnings.push({
      kind: 'programs',
      count,
      message: `${count} ${pluralizeRu(count, ['программа', 'программы', 'программ'])} ${pluralizeWithoutDatesLabel(count)}`,
      entityHref: resolveUndatedEntityHref('programs', undatedPrograms),
    });
  }

  if (weeksCount) {
    const count = weeksCount;
    warnings.push({
      kind: 'weeks',
      count,
      message: `${count} ${pluralizeRu(count, ['неделя', 'недели', 'недель'])} ${pluralizeWithoutDatesLabel(count)}`,
      entityHref: resolveUndatedEntityHref('weeks', undatedWeeks),
    });
  }

  if (workoutsCount) {
    const count = workoutsCount;
    warnings.push({
      kind: 'workouts',
      count,
      message: `${count} ${pluralizeRu(count, ['тренировка', 'тренировки', 'тренировок'])} ${pluralizeWithoutDatesLabel(count)}`,
      entityHref: resolveUndatedEntityHref('workouts', undatedWorkouts),
    });
  }

  return warnings;
}

function collectUndatedWorkouts(entries) {
  const undated = [];
  const seen = new Set();

  for (const workout of findUndatedSection(entries)?.workouts || []) {
    const id = workout?.id;
    if (!id || seen.has(id)) {
      continue;
    }
    if (String(workout?.date_sort ?? '').trim()) {
      continue;
    }
    seen.add(id);
    undated.push(workout);
  }

  return undated;
}

function countProgramProgress(program) {
  const workouts = collectProgramWorkouts(program);
  const total = workouts.length;
  const completed = workouts.filter((workout) => workout.status === 'completed').length;
  return { total, completed };
}

function resolveProgramWorkoutFocusState(workout) {
  if (workout?.status === 'completed') {
    return 'completed';
  }
  if (isWorkoutMissingPlanSlot(workout)) {
    return 'planned';
  }
  return 'scheduled';
}

function buildClientTrainingPlanWeekUrl(clientUuid, weekDateStart) {
  const uuid = String(clientUuid || '').trim();
  const weekDate = String(weekDateStart || '').trim().slice(0, 10);
  if (!uuid || !/^\d{4}-\d{2}-\d{2}$/.test(weekDate)) {
    return '';
  }

  return `/clients/${uuid}/training-plan?week_date=${weekDate}`;
}

function addDaysToProgramYmd(ymd, days) {
  const normalized = String(ymd ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return '';
  }

  const date = new Date(`${normalized}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function resolveProgramFocusPhaseStartWeekIndices(program) {
  const indices = new Set();
  let nextStartIndex = 1;

  for (const phase of program?.phases || []) {
    const weeksCountFromMeta = Number(phase.weeks_count) || 0;
    const visibleWeekCount = Array.isArray(phase.weeks) ? phase.weeks.length : 0;
    const weeksInPhase = weeksCountFromMeta > 0 ? weeksCountFromMeta : visibleWeekCount;

    if (weeksInPhase <= 0) {
      continue;
    }

    indices.add(nextStartIndex);
    nextStartIndex += weeksInPhase;
  }

  return indices;
}

function expandProgramFocusWeekRows(flatWeeks, {
  totalWeeks,
  program,
  selectedWeekBounds,
  displayWeekIndex,
  programUuid,
  programOpenUrl,
  clientUuid,
  isFocusedProgram,
  summary,
  activeProgram,
  viewerTodayYmd = formatTodayYmd(),
}) {
  const targetWeekCount = Number(totalWeeks) || 0;
  if (targetWeekCount <= flatWeeks.length) {
    return flatWeeks;
  }

  const weeksByIndex = new Map();
  for (const weekRow of flatWeeks) {
    weeksByIndex.set(Number(weekRow.index) || weeksByIndex.size + 1, weekRow);
  }

  const programStartYmd = resolveProgramDateStartYmd(program);
  const phaseStartWeekIndices = resolveProgramFocusPhaseStartWeekIndices(program);
  const expanded = [];

  for (let index = 1; index <= targetWeekCount; index += 1) {
    const existing = weeksByIndex.get(index);
    if (existing) {
      expanded.push({
        ...existing,
        isPhaseStart: phaseStartWeekIndices.has(index),
      });
      continue;
    }

    const weekStartYmd = programStartYmd
      ? addDaysToProgramYmd(programStartYmd, (index - 1) * 7)
      : '';
    const weekEndYmd = weekStartYmd
      ? addDaysToProgramYmd(weekStartYmd, 6)
      : '';

    const state = resolveProgramFocusWeekState({ week_index: index }, {
      selectedWeekBounds: weekStartYmd && weekEndYmd
        ? { dateStart: weekStartYmd, dateEnd: weekEndYmd }
        : selectedWeekBounds,
      displayWeekIndex,
    });
    const calendarState = resolveProgramFocusWeekCalendarState(
      { week_index: index, date_start_raw: weekStartYmd, date_end_raw: weekEndYmd },
      viewerTodayYmd,
    );

    expanded.push({
      index,
      name: `Неделя ${index}`,
      phaseTitle: resolvePhaseTitleForWeek(program, { week_index: index }),
      periodLabel: formatWeekPeriodCompact(weekStartYmd, weekEndYmd) || '—',
      state,
      isViewed: state === 'current',
      calendarState,
      workoutsCompleted: 0,
      workoutsScheduled: 0,
      workoutsPlanned: 0,
      openUrl: getWeekOpenHref({ week_index: index }, programUuid) || programOpenUrl,
      trainingPlanWeekUrl: buildClientTrainingPlanWeekUrl(clientUuid, weekStartYmd),
      isSkipWeek: false,
      isPhaseStart: phaseStartWeekIndices.has(index),
    });
  }

  return expanded;
}

function resolveProgramFocusWeekCalendarState(week, todayYmd) {
  const weekEnd = resolveWeekDateEndYmd(week);
  const weekStart = resolveWeekDateStartYmd(week);

  if (weekEnd && todayYmd > weekEnd) {
    return 'past';
  }
  if (weekStart && todayYmd < weekStart) {
    return 'upcoming';
  }
  if (weekStart && weekEnd) {
    return 'current';
  }

  if (week?.is_active) {
    return 'current';
  }

  return 'upcoming';
}

function resolveProgramFocusWeekState(week, {
  selectedWeekBounds,
  displayWeekIndex,
}) {
  if (selectedWeekBounds && weekOverlapsBounds(week, selectedWeekBounds)) {
    return 'current';
  }

  const weekEnd = resolveWeekDateEndYmd(week);
  const weekStart = resolveWeekDateStartYmd(week);
  if (weekEnd && selectedWeekBounds?.dateStart && weekEnd < selectedWeekBounds.dateStart) {
    return 'past';
  }
  if (weekStart && selectedWeekBounds?.dateEnd && weekStart > selectedWeekBounds.dateEnd) {
    return 'upcoming';
  }

  const weekIndex = Number(week?.week_index) || 0;
  if (weekIndex && displayWeekIndex) {
    if (weekIndex < displayWeekIndex) {
      return 'past';
    }
    if (weekIndex === displayWeekIndex) {
      return 'current';
    }
    return 'upcoming';
  }

  if (week?.is_active) {
    return 'current';
  }

  return 'upcoming';
}

export function buildClientTrainingPlanProgramFocusViewModel(program, {
  context,
  selectedWeekBounds,
  clientUuid,
  viewerTimezone,
} = {}) {
  if (!program) {
    return null;
  }

  const viewerTodayYmd = formatTodayYmd(viewerTimezone);

  const ctx = context || resolveProgramFocusContext(program, {
    summary: null,
    activeProgram: null,
  });
  const {
    currentWeekIndex,
    hasCalendarDates,
    weeksTotal,
    isFocusedProgram,
    summary,
    activeProgram,
  } = {
    ...ctx,
    summary: context?.summary ?? null,
    activeProgram: context?.activeProgram ?? null,
  };

  const activeWeekInView = selectedWeekBounds
    ? findProgramWeekInBounds(program, selectedWeekBounds)
    : findActiveWeek(program, currentWeekIndex, hasCalendarDates, viewerTimezone);

  let displayWeekIndex = Number(currentWeekIndex) || 0;
  if (activeWeekInView) {
    displayWeekIndex = Number(activeWeekInView.week_index) || displayWeekIndex;
  }

  const currentPhaseIndex = activeWeekInView
    ? findPhaseIndexForWeek(program, activeWeekInView)
    : resolveCurrentPhaseIndex(program, {
      summary,
      activeProgram,
      isFocusedProgram,
    });

  const totalPhases = countProgramPhases(program);
  const totalWeeks = weeksTotal || resolveProgramWeeksTotal(program, {
    summary,
    activeProgram,
    isFocusedProgram,
  });
  const progress = countProgramProgress(program);
  const programUuid = String(program?.program_uuid ?? program?.id ?? '').trim();
  const programOpenUrl = program?.open_url
    || program?.program_detail_url
    || (programUuid ? `/training-programs/${programUuid}` : '');

  const flatWeeks = [];
  let maxWeekWorkoutCount = 0;
  const todayYmd = viewerTodayYmd;
  const phaseStartWeekIndices = resolveProgramFocusPhaseStartWeekIndices(program);

  for (const phase of program?.phases || []) {
    for (const week of phase.weeks || []) {
      const weekWorkouts = week.workouts || [];
      const workoutsCompleted = weekWorkouts.filter((workout) => workout.status === 'completed').length;
      const workoutsScheduled = weekWorkouts.length;
      maxWeekWorkoutCount = Math.max(maxWeekWorkoutCount, workoutsScheduled);

      const weekIndex = Number(week.week_index) || flatWeeks.length + 1;
      const state = resolveProgramFocusWeekState(week, {
        selectedWeekBounds,
        displayWeekIndex,
      });
      const calendarState = resolveProgramFocusWeekCalendarState(week, todayYmd);
      const skipWeek = isSkipWeek(week);

      flatWeeks.push({
        index: weekIndex,
        name: resolveProgramFocusWeekDisplayName(week, weekIndex, {
          isViewedWeek: state === 'current',
          isSkipWeek: skipWeek,
          weeksTotal,
          summary,
          activeProgram,
          isFocusedProgram,
        }),
        phaseTitle: String(phase.title ?? phase.name ?? '').trim() || resolvePhaseTitleForWeek(program, week),
        periodLabel: formatWeekPeriodCompact(
          resolveWeekDateStartYmd(week),
          resolveWeekDateEndYmd(week),
        ) || '—',
        state,
        isViewed: state === 'current',
        calendarState,
        workoutsCompleted: skipWeek ? 0 : workoutsCompleted,
        workoutsScheduled: skipWeek ? 0 : workoutsScheduled,
        workoutsPlanned: 0,
        openUrl: getWeekOpenHref(week, programUuid) || week?.open_url || programOpenUrl,
        trainingPlanWeekUrl: buildClientTrainingPlanWeekUrl(
          clientUuid,
          resolveWeekDateStartYmd(week),
        ),
        isSkipWeek: skipWeek,
        isPhaseStart: phaseStartWeekIndices.has(weekIndex),
      });
    }
  }

  const plannedWorkoutsPerWeek = maxWeekWorkoutCount
    || Math.max(1, Math.ceil(progress.total / Math.max(flatWeeks.length, 1)));

  for (const weekRow of flatWeeks) {
    if (weekRow.isSkipWeek) {
      weekRow.workoutsPlanned = 0;
      continue;
    }
    weekRow.workoutsPlanned = Math.max(weekRow.workoutsScheduled, plannedWorkoutsPerWeek);
  }

  const expandedWeeks = expandProgramFocusWeekRows(flatWeeks, {
    totalWeeks,
    program,
    selectedWeekBounds,
    displayWeekIndex,
    programUuid,
    programOpenUrl,
    clientUuid,
    isFocusedProgram,
    summary,
    activeProgram,
    viewerTodayYmd,
  });

  for (const weekRow of expandedWeeks) {
    if (weekRow.isSkipWeek) {
      weekRow.workoutsPlanned = 0;
      continue;
    }
    if (!weekRow.workoutsPlanned) {
      weekRow.workoutsPlanned = Math.max(weekRow.workoutsScheduled, plannedWorkoutsPerWeek);
    }
  }

  const programWorkoutsCompletedTotal = expandedWeeks
    .filter((weekRow) => !weekRow.isSkipWeek)
    .reduce((sum, weekRow) => sum + (Number(weekRow.workoutsCompleted) || 0), 0);
  const programWorkoutsPlannedTotal = expandedWeeks
    .filter((weekRow) => !weekRow.isSkipWeek)
    .reduce((sum, weekRow) => sum + (Number(weekRow.workoutsPlanned) || 0), 0);

  const phases = (program?.phases || []).map((phase, index) => ({
    index: index + 1,
    name: String(phase.title ?? phase.name ?? '').trim() || `Фаза ${index + 1}`,
    weekCount: Number(phase.weeks_count) || phase.weeks?.length || 0,
    state: index + 1 < currentPhaseIndex
      ? 'past'
      : index + 1 === currentPhaseIndex
        ? 'current'
        : 'upcoming',
  }));

  const workouts = [];
  let workoutGlobalIndex = 0;

  for (const phase of program?.phases || []) {
    for (const week of phase.weeks || []) {
      let slotIndex = 0;
      for (const workout of week.workouts || []) {
        workoutGlobalIndex += 1;
        slotIndex += 1;
        const workoutTitle = String(workout.title ?? workout.name ?? '').trim();
        workouts.push({
          index: workoutGlobalIndex,
          weekIndex: Number(week.week_index) || 0,
          slotIndex,
          label: workoutTitle || `Неделя ${week.week_index || slotIndex} · тренировка ${slotIndex}`,
          state: resolveProgramWorkoutFocusState(workout),
          openUrl: resolveWorkoutPlanRowHref(workout) || programOpenUrl,
        });
      }
    }
  }

  const programStartDateLabel = formatPlanDateCompact(resolveProgramDateStartYmd(program)) || '—';
  const programEndDateLabel = formatPlanDateCompact(resolveProgramDateEndYmd(program)) || '—';
  const programEndYmd = resolveProgramDateEndYmd(program);
  const programEndReached = /^\d{4}-\d{2}-\d{2}$/.test(programEndYmd)
    && todayYmd >= programEndYmd;

  return {
    title: String(program?.title ?? '').trim() || '—',
    programOpenUrl,
    currentPhaseTitle: activeWeekInView
      ? resolvePhaseTitleForWeek(program, activeWeekInView)
      : resolveCurrentPhaseTitle(program, {
        summary,
        activeProgram,
        isFocusedProgram,
      }),
    currentPhaseIndex,
    totalPhases,
    currentWeekTitle: activeWeekInView
      ? resolveCurrentWeekTitle({
        summary: isFocusedProgram ? summary : null,
        activeProgram: isFocusedProgram ? activeProgram : null,
        activeWeek: activeWeekInView,
      })
      : resolveCurrentWeekTitle({
        summary: isFocusedProgram ? summary : null,
        activeProgram: isFocusedProgram ? activeProgram : null,
        activeWeek: null,
      }),
    currentWeekIndex: displayWeekIndex,
    totalWeeks,
    programWorkoutsPlannedTotal,
    programWorkoutsScheduledTotal: progress.total,
    programWorkoutsCompletedTotal,
    programStartDateLabel,
    programEndDateLabel,
    programEndReached,
    phases,
    weeks: expandedWeeks,
    workouts,
  };
}

/**
 * Режим отображения блоков программы и недели на странице плана.
 *
 * @param {?{ has_programs?: boolean, has_weeks?: boolean, has_workouts?: boolean }} contentHistory
 * @return {{ showProgramsSection: boolean, showWeeksSection: boolean }}
 */
export function resolveTrainingPlanSectionVisibility(contentHistory) {
  const hasPrograms = Boolean(contentHistory?.has_programs);
  const hasWeeksOrWorkouts = Boolean(contentHistory?.has_weeks) || Boolean(contentHistory?.has_workouts);

  if (hasPrograms) {
    return {
      showProgramsSection: true,
      showWeeksSection: true,
    };
  }

  if (hasWeeksOrWorkouts) {
    return {
      showProgramsSection: false,
      showWeeksSection: true,
    };
  }

  return {
    showProgramsSection: true,
    showWeeksSection: false,
  };
}

export function buildClientTrainingPlanMainViewModel({
  summary,
  entries,
  activeProgram,
  selectedWeekBounds,
  undatedCounts = null,
  contentHistory = null,
  clientUuid,
  viewerTimezone,
}) {
  const viewerTodayYmd = formatTodayYmd(viewerTimezone);
  const weekBounds = selectedWeekBounds || resolveDefaultSelectedWeekBounds();
  const allPrograms = sortProgramsForDisplay(collectAllPrograms(entries));
  const activePrograms = weekBounds
    ? allPrograms.filter((program) => programOverlapsWeekBounds(program, weekBounds))
    : [];
  const showProgramIndex = activePrograms.length > 1;

  const activeProgramRows = activePrograms.map((program, index) => {
    const context = {
      ...resolveProgramFocusContext(program, { summary, activeProgram }),
      summary,
      activeProgram,
    };

    return {
      program,
      context,
      programIndex: index + 1,
    };
  });

  const activeProgramCards = activeProgramRows.map((row) => ({
    ...buildActiveProgramCardViewModel(
      row.program,
      row.context,
      {
        programIndex: row.programIndex,
        showProgramIndex,
        selectedWeekBounds: weekBounds,
        viewerTimezone,
      },
    ),
    focusView: buildClientTrainingPlanProgramFocusViewModel(row.program, {
      context: row.context,
      selectedWeekBounds: weekBounds,
      clientUuid,
      viewerTimezone,
    }),
  }));

  const nonProgramWeekWorkouts = weekBounds
    ? collectNonProgramWorkoutsInWeekBounds(entries, weekBounds)
    : [];
  const outsideProgramsFocusView = activeProgramCards.length > 0 && nonProgramWeekWorkouts.length > 0
    ? buildClientTrainingPlanOutsideProgramsFocusViewModel(nonProgramWeekWorkouts, weekBounds, viewerTimezone)
    : null;

  const selectedWeekSection = weekBounds
    ? buildSelectedWeekSection(entries, weekBounds, {
      summary,
      activeProgram,
      programsForWeek: activePrograms,
      viewerTodayYmd,
    })
    : null;

  const hasSelectedWeekContent = Boolean(
    selectedWeekSection
    && (
      selectedWeekSection.weekHeaders.length > 0
      || selectedWeekSection.workouts.length > 0
      || selectedWeekSection.isAllSkipWeeks
    ),
  );

  const undatedPrograms = collectUndatedPrograms(entries);
  const undatedWeeks = collectUndatedWeeks(entries);
  const undatedWorkouts = collectUndatedWorkouts(entries);
  const undatedWarnings = buildUndatedPlanWarnings({
    undatedPrograms,
    undatedWeeks,
    undatedWorkouts,
    undatedCounts,
  });

  const sectionVisibility = resolveTrainingPlanSectionVisibility(contentHistory);

  return {
    hasProgram: allPrograms.length > 0,
    hasActivePrograms: activeProgramCards.length > 0,
    hasUpcomingWorkouts: hasSelectedWeekContent,
    activePrograms: activeProgramCards,
    outsideProgramsFocusView,
    selectedWeekSection,
    undatedPrograms,
    undatedWeeks,
    undatedWorkouts,
    undatedWarnings,
    sectionVisibility,
  };
}
